import { Phone, Mail, MessageCircle, MapPin, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";
import PageSeo from "@/components/PageSeo";

export default function Contact() {
  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <PageSeo
        title="Contact The Barracks Bistro — Phone, WhatsApp, Directions"
        description="Phone, email, WhatsApp and directions to The Barracks Bistro in Wellington, Nilgiris. Open for lunch, dinner and private events."
        path="/contact"
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="eyebrow">Contact</div>
        <h1 className="mt-3 text-5xl sm:text-6xl max-w-3xl leading-[1]">
          Say hello. <span className="italic text-[hsl(var(--brass))]">Or just drop in.</span>
        </h1>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="space-y-6">
            <ContactRow icon={MessageCircle} label="WhatsApp" testid="contact-whatsapp"
              href={SITE.whatsapp} value={SITE.phonePrimary} external />
            <ContactRow icon={Phone} label="Phone" testid="contact-phone-primary"
              href={`tel:${SITE.phonePrimary.replace(/\s/g, "")}`} value={SITE.phonePrimary} />
            <ContactRow icon={Phone} label="Phone (alt)" testid="contact-phone-secondary"
              href={`tel:${SITE.phoneSecondary.replace(/\s/g, "")}`} value={SITE.phoneSecondary} />
            <ContactRow icon={Mail} label="Email" testid="contact-email"
              href={`mailto:${SITE.email}`} value={SITE.email} />
            <ContactRow icon={MapPin} label="Directions" testid="contact-maps"
              href={SITE.maps} value={SITE.address} external />
          </div>

          <div className="bg-[hsl(var(--bg-2))] p-8">
            <div className="eyebrow">Opening Hours</div>
            <ul className="mt-4 divide-y divide-[hsl(var(--rule))]">
              {SITE.hours.map((h) => (
                <li key={h.day} className="py-3 flex justify-between items-baseline">
                  <span className="text-lg">{h.day}</span>
                  <span className="text-[hsl(var(--ink-soft))]">{h.time}</span>
                </li>
              ))}
            </ul>
            <div className="rule-brass my-6" />
            <p className="text-sm text-[hsl(var(--ink-soft))]">
              Kitchen closes 30 minutes before we do. Bonfire on weekends, weather permitting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testid }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
      className="flex items-start gap-4 border-b border-[hsl(var(--rule))] pb-5 group"
    >
      <Icon size={20} className="mt-1 text-[hsl(var(--brass))]" />
      <div className="flex-1">
        <div className="eyebrow">{label}</div>
        <div className="mt-1 text-lg group-hover:text-[hsl(var(--brass-2))] transition-colors">{value}</div>
      </div>
      {external && <ExternalLink size={14} className="mt-2 text-[hsl(var(--ink-soft))]" />}
    </a>
  );
}
